<p-toast></p-toast>

<app-page-shell>
    <app-page-hero
        eyebrow="Finance Command Center"
        title="Logistics Accounting Overview"
        [subtitle]="companyName + ' | ' + periodLabel + ' | refreshed ' + refreshedLabel">
        <div nav class="hero-nav">
            <button *ngIf="canViewInvoices" pButton type="button" label="Invoices" icon="pi pi-file" [outlined]="true" (click)="openRoute('/accounts/enterprise/receivables/generated-invoices')"></button>
            <button *ngIf="canViewBills" pButton type="button" label="Bills" icon="pi pi-truck" [outlined]="true" (click)="openRoute('/accounts/enterprise/payables/generated-bills')"></button>
            <button *ngIf="canViewBills || canViewVouchers" pButton type="button" label="Payment Runs" icon="pi pi-send" [outlined]="true" (click)="openRoute('/accounts/enterprise/payables/payment-proposals')"></button>
            <button *ngIf="canViewReports" pButton type="button" label="Reports" icon="pi pi-chart-line" [outlined]="true" (click)="openRoute('/accounts/enterprise/reports/profit-and-loss')"></button>
        </div>

        <div actions class="dashboard-actions">
            <label class="date-field">
                <span>From</span>
                <p-datepicker [(ngModel)]="startDate" dateFormat="yy-mm-dd" [showIcon]="true" [appendTo]="'body'"></p-datepicker>
            </label>
            <label class="date-field">
                <span>To</span>
                <p-datepicker [(ngModel)]="endDate" dateFormat="yy-mm-dd" [showIcon]="true" [appendTo]="'body'"></p-datepicker>
            </label>
            <button pButton type="button" label="Refresh" icon="pi pi-refresh" [loading]="loading" (click)="load()"></button>
            <button pButton type="button" label="Export" icon="pi pi-download" [outlined]="true" (click)="exportSnapshot()" [disabled]="!dashboard"></button>
        </div>
    </app-page-hero>

    <app-dashboard-company-scope-selector
        *ngIf="showGroupCompanySelector"
        [companyGroupName]="activeGroupName"
        [memberCompanies]="groupCompanies"
        [selectedCompanyId]="selectedGroupCompanyId"
        (selectedCompanyIdChange)="onDashboardCompanyScopeChange($event)">
    </app-dashboard-company-scope-selector>

    <section class="loading-state" *ngIf="loading && !dashboard">
        <p-progressSpinner></p-progressSpinner>
        <div>
            <strong>Loading finance command center</strong>
            <p>Aggregating AR, AP, cash, ZATCA, matching, payment-run, and logistics profitability signals.</p>
        </div>
    </section>

    <section class="error-state" *ngIf="loadError && !loading">
        <i class="pi pi-exclamation-triangle"></i>
        <div>
            <strong>{{ loadError }}</strong>
            <p>The dashboard endpoint returns safe zero values for empty modules, so this usually means a backend or company-context issue.</p>
        </div>
        <button pButton type="button" label="Retry" icon="pi pi-refresh" (click)="load()"></button>
    </section>

    <ng-container *ngIf="dashboard">
        <app-page-metric-strip [metrics]="headlineMetrics"></app-page-metric-strip>

        <section class="executive-grid" aria-label="Executive finance scorecards">
            <article
                *ngFor="let kpi of executiveCards"
                [ngClass]="cardClass(kpi)"
                [class.clickable]="kpi.route"
                (click)="openRoute(kpi.route)">
                <div class="kpi-topline">
                    <span>{{ kpi.label }}</span>
                    <i class="pi" [ngClass]="{
                        'pi-arrow-up-right': kpi.severity === 'success',
                        'pi-exclamation-triangle': kpi.severity === 'danger' || kpi.severity === 'warn',
                        'pi-chart-line': kpi.severity === 'info' || !kpi.severity || kpi.severity === 'default'
                    }"></i>
                </div>
                <strong>{{ kpiDisplay(kpi) }}</strong>
                <p>{{ kpi.hint || 'Current dashboard period' }}</p>
                <span class="trend-chip" *ngIf="kpi.trendPercent !== undefined && kpi.trendPercent !== null" [class.negative]="kpi.trendPercent < 0">
                    {{ kpi.trendPercent > 0 ? '+' : '' }}{{ percent(kpi.trendPercent) }}
                </span>
            </article>
        </section>

        <section class="operations-grid">
            <app-page-data-panel title="Receivables Control" subtitle="Invoice readiness, collections pressure, unapplied cash, and Saudi e-invoice exposure.">
                <div class="compact-kpi-grid">
                    <button type="button" class="compact-kpi" *ngFor="let kpi of receivableCards" (click)="openRoute(kpi.route)" [class.clickable]="kpi.route">
                        <span>{{ kpi.label }}</span>
                        <strong>{{ kpiDisplay(kpi) }}</strong>
                        <small>{{ kpi.hint }}</small>
                    </button>
                </div>
            </app-page-data-panel>

            <app-page-data-panel title="Payables Control" subtitle="Bill readiness, match exceptions, payment holds, due-week pressure, and released payment runs.">
                <div class="compact-kpi-grid">
                    <button type="button" class="compact-kpi" *ngFor="let kpi of payableCards" (click)="openRoute(kpi.route)" [class.clickable]="kpi.route">
                        <span>{{ kpi.label }}</span>
                        <strong>{{ kpiDisplay(kpi) }}</strong>
                        <small>{{ kpi.hint }}</small>
                    </button>
                </div>
            </app-page-data-panel>

            <app-page-data-panel title="Control Tower" subtitle="Accounting controls that should be clean before month-end close.">
                <div class="compact-kpi-grid">
                    <button type="button" class="compact-kpi" *ngFor="let kpi of controlCards" (click)="openRoute(kpi.route)" [class.clickable]="kpi.route">
                        <span>{{ kpi.label }}</span>
                        <strong>{{ kpiDisplay(kpi) }}</strong>
                        <small>{{ kpi.hint }}</small>
                    </button>
                </div>
            </app-page-data-panel>
        </section>

        <section class="chart-grid" *ngIf="chartEntries.length">
            <app-page-data-panel *ngFor="let entry of chartEntries" [title]="entry.chart.title || entry.key">
                <div class="chart-frame" [class.compact-chart]="entry.chart.type === 'doughnut' || entry.chart.type === 'pie'">
                    <p-chart [type]="chartType(entry.chart)" [data]="chartData(entry.chart)" [options]="chartOptionsFor(entry.chart)"></p-chart>
                </div>
            </app-page-data-panel>
        </section>

        <app-page-data-panel title="Logistics Profitability" subtitle="Revenue, cost, and margin grouped by logistics source type for the selected company scope.">
            <p-table [value]="logisticsRows" [tableStyle]="{ 'min-width': '760px' }" styleClass="p-datatable-sm">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Source</th>
                        <th class="text-right">Documents</th>
                        <th class="text-right">Revenue</th>
                        <th class="text-right">Cost</th>
                        <th class="text-right">Margin</th>
                        <th class="text-right">Margin %</th>
                        <th>Action</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td>
                            <div class="source-cell">
                                <span>{{ row.sourceType || 'UNKNOWN' }}</span>
                                <small>Shipment, order, clearance, or manual finance source</small>
                            </div>
                        </td>
                        <td class="text-right">{{ row.documentCount || 0 }}</td>
                        <td class="text-right">{{ money(row.revenueAmount) }}</td>
                        <td class="text-right">{{ money(row.costAmount) }}</td>
                        <td class="text-right" [class.negative-amount]="(row.marginAmount || 0) < 0">{{ money(row.marginAmount) }}</td>
                        <td class="text-right" [class.negative-amount]="(row.marginPercent || 0) < 0">{{ percent(row.marginPercent) }}</td>
                        <td>
                            <button pButton type="button" label="Open" icon="pi pi-external-link" [text]="true" (click)="openRoute(row.route)"></button>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="7" class="empty-cell">No logistics profitability rows were found for this period.</td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>

        <section class="queue-grid">
            <app-page-data-panel
                *ngFor="let section of queueSections"
                [title]="section.title"
                [resultText]="section.rows.length + ' items'">
                <div class="empty-queue" *ngIf="!section.rows.length">
                    <i class="pi pi-check-circle"></i>
                    <span>No urgent items in this queue.</span>
                </div>

                <div class="queue-list" *ngIf="section.rows.length">
                    <button type="button" class="queue-row" *ngFor="let item of section.rows" (click)="openRoute(item.route)">
                        <span class="queue-main">
                            <strong>{{ item.title || item.documentNumber || item.documentType }}</strong>
                            <small>{{ item.subtitle || item.reason || item.documentNumber }}</small>
                        </span>
                        <span class="queue-meta">
                            <p-tag [value]="item.status || item.severity || 'OPEN'" [severity]="statusSeverity(item.status || item.severity)"></p-tag>
                            <span *ngIf="item.amount !== undefined && item.amount !== null">{{ money(item.amount, item.currencyCode) }}</span>
                            <span *ngIf="item.count !== undefined && item.count !== null">{{ item.count }} items</span>
                        </span>
                    </button>
                </div>
            </app-page-data-panel>
        </section>
    </ng-container>
</app-page-shell>
